import { environment } from "@/environments/environment";
import {
  closeVideoModal,
  selectActiveVideoTab,
  selectIsVideoModalOpen,
  setActiveVideoTab,
  VideoModalTab,
} from "@/store/slices/videoModalSlice";
import {
  CheckCircle,
  X,
  Play,
  Pause,
  Maximize2,
  Minimize2,
  Info,
  Rewind,
  FastForward,
} from "lucide-react";
import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Video tutorial data
interface VideoTutorialData {
  title: string;
  videoUrl: string;
  description: string[];
}

const VIDEO_TUTORIALS: Record<VideoModalTab, VideoTutorialData> = {
  mockup: {
    title: "Mockup Studio Guide",
    videoUrl: `https://360customizer.360customizer.com/${environment.S3_FOLDER_NAME}/site-content/tool-guide/mockup_guide.mp4`,
    description: [
      "Go To Products and select product of your choice",
      "See mockup enabled button on top and click on it",
      "Upload logo of your brand and click on generate after choosing mockup of your choice",
      "Your mockup will be ready and downloaded",
    ],
  },
  "catalog-builder": {
    title: "Catalog Builder Guide",
    videoUrl: `https://360customizer.360customizer.com/${environment.S3_FOLDER_NAME}/site-content/tool-guide/catalog_guide.mp4`,
    description: [
      "Build professional catalogs in minutes",
      "Add products with descriptions and pricing",
      "Organize items with categories and filters",
      "Share or download your catalog instantly",
    ],
  },
};

const VideoModal: React.FC = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsVideoModalOpen);
  const activeTab: string = useSelector(selectActiveVideoTab);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentTutorial = VIDEO_TUTORIALS[activeTab];

  // Reset video when modal opens or tab changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [isOpen, activeTab]);

  // Disable background scroll + clicks
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");
    } else {
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    };
  }, [isOpen]);

  // Detect fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleClose = () => {
    dispatch(closeVideoModal());
  };

  const handleTabChange = (tab: VideoModalTab) => {
    dispatch(setActiveVideoTab(tab));
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const seekBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(
        0,
        videoRef.current.currentTime - 5
      );
    }
  };

  const seekForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration || 0,
        videoRef.current.currentTime + 5
      );
    }
  };

  const handleFullscreen = async () => {
    if (videoRef.current) {
      try {
        if (!document.fullscreenElement) {
          await videoRef.current.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch (err) {
        console.error("Fullscreen error:", err);
      }
    }
  };

  const handleVideoClick = () => togglePlayPause();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn pointer-events-auto"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[80vh] md:h-[90vh] overflow-hidden flex flex-col animate-slideUp pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Tabs */}
        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex space-x-1">
              {(Object.keys(VIDEO_TUTORIALS) as VideoModalTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    activeTab === tab
                      ? "bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 text-white shadow-md"
                      : "text-gray-600 hover:bg-white hover:text-blue-600"
                  }`}
                >
                  {tab
                    .split("-")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </button>
              ))}
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-full"
            >
              <X />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8">
          {/* Title */}
          <div className="text-center mb-6 lg:mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-800">
              {currentTutorial.title}
            </h2>
          </div>

          <div className="grid lg:grid-cols-[2fr_1fr] gap-6 lg:gap-8">
            {/* Video Section */}
            <div className="space-y-4">
              <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-lg group h-80 flex items-center justify-center">
                <video
                  ref={videoRef}
                  src={currentTutorial.videoUrl}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={handleVideoClick}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />

                {/* Video Controls */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={seekBackward}
                        className="text-white hover:text-blue-400 p-2 bg-black/30 rounded-full backdrop-blur-sm"
                      >
                        <Rewind className="w-5 h-5" />
                      </button>
                      <button
                        onClick={togglePlayPause}
                        className="text-white hover:text-blue-400 p-2 bg-black/30 rounded-full backdrop-blur-sm"
                      >
                        {isPlaying ? (
                          <Pause className="w-6 h-6" />
                        ) : (
                          <Play className="w-6 h-6" />
                        )}
                      </button>
                      <button
                        onClick={seekForward}
                        className="text-white hover:text-blue-400 p-2 bg-black/30 rounded-full backdrop-blur-sm"
                      >
                        <FastForward className="w-5 h-5" />
                      </button>
                    </div>

                    <button
                      onClick={handleFullscreen}
                      className="text-white hover:text-blue-400 p-2 bg-black/30 rounded-full backdrop-blur-sm"
                    >
                      {isFullscreen ? (
                        <Minimize2 className="w-6 h-6" />
                      ) : (
                        <Maximize2 className="w-6 h-6" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Play Icon Overlay */}
                {!isPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-blue-600 rounded-full p-5 shadow-2xl animate-pulse">
                      <Play className="w-12 h-12 text-white" />
                    </div>
                  </div>
                )}
              </div>

              {/* Info Card */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Click on the video to play or pause. Use fullscreen for the
                    best experience.
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 lg:p-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <CheckCircle className="mr-2 " /> What You'll Learn{" "}
                </h3>
                <ul className="space-y-3">
                  {currentTutorial.description.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start text-gray-700 leading-relaxed"
                    >
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-medium mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .modal-open {
          pointer-events: none !important;
        }
        .modal-open .fixed {
          pointer-events: auto !important;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default VideoModal;
